import React, { FC, memo, useCallback, useState } from 'react'
import useTheme from '@sport1/react-elements/useTheme'
import Touchable from '@sport1/news-styleguide/Touchable'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import FontelloIcon from '@sport1/news-styleguide/FontelloIcon'
import Tonal from '@sport1/news-styleguide/Tonal'
import Button from '@sport1/news-styleguide/Button'
import colorPattern from '@sport1/news-styleguide/colorPattern'
import { ThemeProps } from '@sport1/news-styleguide/Types'
import { useRouter } from 'next/router'
import { isPianoUserLoggedIn, pianoUserLogin } from '@/components/ThirdParty/Piano/util'
import AccountOverlay from '@/components/ThirdParty/Piano/Composer/components/AccountOverlay'
import { disableInactivePopup, enableInactivePopup } from '@/components/InactiveHomePopup/utils'
import TabbableArea from '@/components/TabbableArea'
import PianoProfileNavigation from '@/components/ThirdParty/Piano/Composer/components/ProfileNavigation'
import { useTracking } from '@/utils/tracking/TrackingProvider'

type PianoHeaderButtonProps = {
    isMobile?: boolean
}

const PianoHeaderButton: FC<PianoHeaderButtonProps> = ({ isMobile = false }) => {
    const { themeName } = (useTheme() as ThemeProps['theme']) || {}
    const [isLoggedIn, setLoggedIn] = useState(isPianoUserLoggedIn())
    const [showOverlayAccount, setShowOverlayAccount] = React.useState(false)
    const [showDropDown, setShowDropDown] = useState(false)
    const [activeBlur, setActiveBlur] = useState(true)
    const { pathname } = useRouter()
    const { trackInteraction } = useTracking()

    const getColor = () => {
        if (isLoggedIn) return colorPattern.sky
        if (themeName === 'dark') return colorPattern.pearl
        return colorPattern.onyx
    }

    const showOverlay = useCallback(
        (trackingLabel?: string) => {
            if (trackingLabel) {
                trackInteraction({
                    devices: 'all',
                    interaction_category: 'navigation',
                    interaction_action: 'click',
                    interaction_label: trackingLabel,
                    platforms: isMobile ? 'mobile' : 'desktop',
                })
            }
            setShowOverlayAccount(prev => !prev)
            !showOverlayAccount ? disableInactivePopup() : enableInactivePopup()
        },
        [isMobile, showOverlayAccount, trackInteraction]
    )

    const callPianoTemplate = useCallback(() => {
        if (!isLoggedIn) {
            trackInteraction({
                devices: 'all',
                interaction_category: 'navigation',
                interaction_action: 'click',
                interaction_label: 'login',
                platforms: isMobile ? 'mobile' : 'desktop',
            })
            pianoUserLogin({
                loginStateCallback: setLoggedIn,
            })
        } else {
            trackInteraction({
                devices: 'all',
                interaction_category: 'navigation',
                interaction_action: 'click',
                interaction_label: 'profile',
                platforms: isMobile ? 'mobile' : 'desktop',
            })
            setShowDropDown(prev => !prev)
        }
    }, [isMobile, isLoggedIn, trackInteraction])

    return (
        <TabbableArea
            testID="piano-header-button"
            style={{
                display: 'flex',
                outline: 'none',
                alignItems: 'center',
            }}
            activateBlur={activeBlur}
            onBlur={() => setShowDropDown(false)}
        >
            <NonFlexingContainer
                position={['absolute', 'absolute', 'relative', 'relative']}
                right={[62, 64, 0, 0]}
                width={[24, 24, '100%']}
                height={[24, 24, '100%']}
                centerHorizontal
                centerVertical
                horizontal
                paddingRight={
                    pathname.includes('tv-video')
                        ? ['spacing-none', 'spacing-11', 'spacing-9', 'spacing-none']
                        : ['spacing-none', 'spacing-none', 'spacing-9', 'spacing-none']
                }
            >
                {isMobile ? (
                    <Touchable onPress={callPianoTemplate}>
                        <FontelloIcon name="account_circle" fontSize="20px" color={getColor()} />
                    </Touchable>
                ) : (
                    <Button
                        text={isLoggedIn ? 'Profil' : 'Login'}
                        buttonType="outlineS"
                        buttonVariant={themeName === 'dark' ? 'primary' : 'tertiary'}
                        onPress={callPianoTemplate}
                    />
                )}
                <PianoProfileNavigation
                    isMobile={isMobile}
                    show={showDropDown}
                    showOverlay={showOverlay}
                    setBlur={setActiveBlur}
                    setShow={setShowDropDown}
                    setLoggedIn={setLoggedIn}
                />
            </NonFlexingContainer>

            {showOverlayAccount && (
                <>
                    <AccountOverlay
                        isMobile={isMobile}
                        onPressCallback={() => showOverlay('piano-setting-close-button')}
                    />
                    <Tonal
                        position="fixed"
                        top={0}
                        left={0}
                        bottom={0}
                        zIndex={2147483646}
                        onPress={() => showOverlay('piano-setting-tonal')}
                        pointerEvents="box-none"
                        testID="piano-overlay-tonal"
                    />
                </>
            )}
        </TabbableArea>
    )
}

export default memo(PianoHeaderButton)
